/**
 * Creates a new Parallax effect on a supplied image
 * based on the relative distance between the container midpoint
 * and the viewport midpoint.
 *
 * @class
 */
export default class Parallax {
  /**
   * Constructs a new Parallax object.
   *
   * @param {Object} options - Set of parameters used to generate the parallax effect.
   * @param {HTMLElement} options.img - The image element to apply the parallax effect to.
   * @param {HTMLElement} options.container - The container element to calculate
   *    parallax effect relative to.
   * @param {Number} [options.parallaxScale=1.5] - The scale to apply to the image,
   *    equates to the area of the image hidden by parallax. Must be a float greater
   *    than 1.
   * @param {Number} [options.parallaxSpeed=1] - The speed of the image movement
   *    relative to user scrolling. Must be a float greater than or equal to 0
   *    and less than or equal to 1. A value of 0 produces a stationary image,
   *    a value of 1 produces an image that moves at the rate of user scrolling.
   * @param {Function} [options.isVisible=()=>true] - A helper function to determine
   *    whether to calculate the parallax effect.
   */
  constructor({
    img,
    container,
    parallaxScale = 1.2,
    parallaxSpeed = 1,
    isVisible = () => true,
  }) {
    this.scaleRegex = /scale\(([1-9]+\.[0-9]+)\)/g;
    this.img = img;
    this.container = container;
    this.isVisible = isVisible;
    this.parallaxScale = parseFloat(parallaxScale);
    this.parallaxSpeed = parseFloat(parallaxSpeed);

    if (!this.img) {
      throw new Error('Parallax: No image supplied.');
    }

    if (!this.container) {
      throw new Error('Parallax: No container supplied.');
    }

    if (this.parallaxScale <= 1) {
      throw new Error('Parallax: parallaxScale must be a value greater than 1.');
    }

    if (this.parallaxSpeed < 0 || this.parallaxSpeed > 1) {
      throw new Error('Parallax: parallaxSpeed must be greater than or equal to 0, and less than or equal to 1.');
    }

    // Useful values that are used in setup and calculation of parallax effect
    this.parallax = this._parallax.bind(this);
    this.parallaxPercentage = 0.00;
    this.interval = null;

    // Image styles needed to create parallax effect
    this.img.style.position = 'absolute';
    this.img.style.top = `calc(50% - ${this.img.getBoundingClientRect().height / 2.5}px)`;
    this.img.style.left = `${(this.parallaxScale / 2) * 100}%`;
    this.img.style.transform = `scale(${this.parallaxScale}) translate3d(-50%, 0, 0)`;
    this.img.style.willChange = 'transform';

    this.interval = setInterval(this.parallax, 10);

    $(window).on('resize', this.parallax);
  }

  unload() {
    $(window).off('resize', this.parallax);

    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  _parallax() {
    if (!this.isVisible()) {
      return;
    }

    this.scaleRegex.lastIndex = 0;

    const imgScale = this.scaleRegex.exec(this.img.style.transform);
    const imgHeight = imgScale && imgScale.length === 2
      ? this.img.getBoundingClientRect().height / parseFloat(imgScale[1])
      : this.img.getBoundingClientRect().height;
    const parallaxTotalHeight = imgHeight * (1 - this.parallaxScale);
    const parallaxHalfHeight = parallaxTotalHeight / 2;
    const windowMidpoint = (window.innerHeight / 2);
    const containerRect = this.container.getBoundingClientRect();
    const containerHalfHeight = containerRect.height / 2;
    const containerMidpoint = containerRect.top + containerHalfHeight;

    let parallaxPercentage = (containerMidpoint - windowMidpoint)
      / (windowMidpoint + containerHalfHeight);

    // Clamp between -1 and 1
    parallaxPercentage = Math.min(Math.max(parallaxPercentage, -1), 1);
    // Percentage should be to nearest two decimal places
    parallaxPercentage = parseFloat(parallaxPercentage.toFixed(2));

    if (this.parallaxPercentage === parallaxPercentage) {
      return;
    }

    this.parallaxPercentage = parallaxPercentage;

    window.requestAnimationFrame(() => {
      const translation = ((parallaxHalfHeight * parallaxPercentage) * this.parallaxSpeed) + parallaxHalfHeight / 2;

      this.img.style.top = `calc(50% - ${this.img.getBoundingClientRect().height / 2.5}px)`;
      this.img.style.transform = `scale(${this.parallaxScale}) translate3d(-50%, ${translation}px, 0)`;
    });
  }
}
